'use client'

import {SidebarInset, SidebarProvider, SidebarTrigger} from "@/components/ui/sidebar";
import {SessionProvider} from "next-auth/react";
import {AppSidebar} from "./components/SideBar";
import {Toaster} from "@/components/ui/toaster";
import {Toaster as SonnerToaster} from "@/components/ui/sonner"
import React, {useEffect, useState} from "react";
import {BreadcrumbsProvider} from "@/context/BreadcrumbsContext";
import {QueryClientProvider} from "@tanstack/react-query";
import {queryClient} from "@/hooks/queries/queryClient";
import {cn} from "@/lib/utils";
import {useViewProps} from "@/hooks/use-view-props";
import {debugKey, setLogLevel} from "@/app/logger";
import {SocketConnectionManager} from "@/app/SocketConnectionManager";
import {useUser, useUserLimits, useUserPreferences} from "@/hooks/queries/use-users";
import {Loader} from "@/app/components/loader";
import {ErrorAlert} from "@/app/components/ErrorAlert";
import {useFeatureFlags} from "@/hooks/queries/use-feature-flags";
import {ClickTracker} from "@/lib/analytics/clickTracker";
import {PostHogAnalytics} from "./components/PostHogAnalytics";
import {TrialExpiredModal} from "./components/TrialExpiredModal";
import {usePublicConfig} from "@/hooks/queries/use-global-config";
import {TosBlockingModal} from "./components/TosBlockingModal";
import {useTosAcceptanceFlow} from "@/hooks/useTosAcceptanceFlow";
import {useOptionalCompanyId} from "@/hooks/use-company-id";
import {usePendingJobToasts} from "@/hooks/use-pending-job-toasts";
import {usePathname} from "next/navigation";
import {matchPath} from "react-router";
import {companyCreatePath, companyOnboardingPath} from "@/app/paths";

const sidebarOpenKey = "sidebarOpen"

export default function App({children, analyticsEnabled = true}: Readonly<{
  children?: React.ReactNode
  analyticsEnabled?: boolean
}>) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const pathname = usePathname()
  const onboarding = pathname === companyCreatePath() ||
    !!matchPath({path: companyOnboardingPath(":companyId")}, pathname)
  useEffect(() => {
    setLogLevel(localStorage.getItem(debugKey) === "true");
  }, []);
  useEffect(() => {
    const saved = localStorage.getItem(sidebarOpenKey)
    setSidebarOpen(saved === null ? true : saved === "true")
  }, []);
  return <div className="h-full overflow-hidden w-full">
    <SocketConnectionManager>
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          {analyticsEnabled && <ClickTracker/>}
          {analyticsEnabled && <PostHogAnalytics/>}
          <BreadcrumbsProvider>
            <SidebarProvider
              defaultOpen={true}
              open={sidebarOpen}
              onOpenChange={(open) => {
                setSidebarOpen(open)
                localStorage.setItem(sidebarOpenKey, open ? "true" : "false")
              }}
              className="h-full overflow-auto"
              style={{
                "--sidebar-width": "250px",
                "--sidebar-width-mobile": "16rem",
              } as never}>
              {!onboarding && <AppSidebar/>}
              <SidebarInset className="h-full flex-col overflow-hidden">
                {!onboarding && <div className="md:hidden bg-sidebar p-2 border-b">
                  <SidebarTrigger/>
                </div>}
                <AppWrapper>{children}</AppWrapper>
              </SidebarInset>
            </SidebarProvider>
          </BreadcrumbsProvider>
        </SessionProvider>
      </QueryClientProvider>
    </SocketConnectionManager>
  </div>
}

function AppWrapper({children}: Readonly<{ children?: React.ReactNode }>) {
  usePublicConfig()
  const {flags, error: flagsError} = useFeatureFlags()
  const {user, error} = useUser()
  useUserLimits()
  useUserPreferences()
  const {standardScroll, standardPadding} = useViewProps()

  const optionalCompanyId = useOptionalCompanyId()
  usePendingJobToasts(optionalCompanyId)

  // Terms of Service (TOS) acceptance flow - handles acceptance and decides modal state
  const tosFlow = useTosAcceptanceFlow()

  const err = error || flagsError
  let content: React.ReactNode;
  if (!user || !flags) {
    content = err ? <ErrorAlert err={err}/> : <Loader/>;
  } else {
    content = <div className="flex flex-1 bg-sidebar h-full overflow-hidden">
      <TosBlockingModal
        shouldShowModal={tosFlow.shouldShowModal}
        submitting={tosFlow.submitting}
        error={tosFlow.error}
        onAccept={tosFlow.acceptFn}
      />
      <div className="flex-1 bg-background overflow-hidden">
        <div
          id="main-scroll-container"
          className={cn(
            "h-full bg-background",
            standardScroll ? "overflow-y-auto overflow-x-hidden" : "overflow-hidden",
            standardPadding && "py-2 px-4",
          )}
        >
          {children}
        </div>
      </div>
      <TrialExpiredModal/>
    </div>;
  }
  return <>
    {content}
    <Toaster/>
    <SonnerToaster/>
  </>
}
